//
// Copyright (c) 2019 Astute Global, Inc.
//

/**
 * Model for a response of a conversation of a topic.
 */
define([
  "backbone",
  "jquery",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/ckeditor5/comments/commentModel",
  "modules/shared/ckeditor5/trackChanges/trackChangesModel",
  "modules/shared/ckeditor5/revisionHistory/revisionHistoryModel",
], function (Backbone, $, Messenger, Properties, CommentModel) {
  "use strict";

  const TopicResponseModel = {};

  const TopicResponseModelResponseTemplateSuper = Backbone.Model.prototype;

  const RESPONSE_PREFIX = "<responsetemplate><agent-utterance>";
  const RESPONSE_SUFFIX = "</agent-utterance></responsetemplate>";

  TopicResponseModel.responseTemplate = Backbone.Model.extend(
    {
      idAttribute: "templateId",
      defaults: function () {
        return {
          actionGroupId: 0,
          associatedContentId: 0,
          languageId: 1,
          response: TopicResponseModel.responseTemplate.EMPTY_RESPONSE_TEXT,
          statusId: 0,
          touchpointId: 0,
          version: 1,
          campaignId: 0,
        };
      },
      initializeCustom: function () {
        this.on(
          "change:statusId",
          function () {
            //also update the status value
            this._updateStatusValue();
          },
          this,
        );
        if (this.get("statusId") > 0) {
          this._updateStatusValue();
        }
      },
      commentThreads: function (options) {
        const subModelOptions = Object.assign(
          {
            modelClass: CommentModel.threads,
            urlExtension: "/commentThreads",
          },
          options,
        );
        this.commentThreadsCol = this.getSubModel2(this.commentThreadsCol, subModelOptions);
        return this.commentThreadsCol;
      },
      isEmpty: function () {
        return this.isResponseTextEmpty() && this.get("associatedContentId") <= 0;
      },
      isResponseTextEmpty: function () {
        const response = this.get("response");
        return (
          !response || TopicResponseModel.responseTemplate.EMPTY_RESPONSE_TEXT_RE.test(response)
        );
      },
      saveChildren: function (options) {
        this.saveSubModels(options);
      },
      deleteCollabData: function () {
        let respTempModel = this;
        return new Promise((resolve, reject) => {
          const kbId = Messenger.reqres.request("active:kb");
          const intentId = Messenger.reqres.request("current:topic:id");
          const templateId = respTempModel.get("templateId");
          if (!intentId || !templateId) {
            // Cannot build a proper URL without IDs.
            // But, a new template also does not have any collaboration data.
            resolve(this);
            return;
          }
          const options = {
            url: `${Properties.rootURL}/${kbId}/intents/${intentId}/templates/${templateId}/deleteCollabData`,
            success: (model, _response) => {
              resolve(model);
            },
            error: (_model, response) => {
              reject(Messenger.getErrorFromResponse(response));
            },
          };
          respTempModel.sync("delete", respTempModel, options);
        });
      },
      validateEmbeddedContent: async function (responseText, responseId, kbId, intentId) {
        return new Promise((resolve) => {
          const model = new Backbone.Model({
            ...this.toJSON(),
            response: responseText,
            isSafe: false,
          });
          const templateId = this.get("templateId");
          if (!intentId || !responseId || !templateId) {
            // Cannot build a proper URL for a new response template.
            // Saving the response template for the first time will also validate the
            // embedded content, so no need to validate it here anyway
            model.set("isSafe", true);
            resolve(model);
            return;
          }
          const url = `${Properties.rootURL}/${kbId}/intents/${intentId}/responses/${responseId}/templates/${templateId}/validate`;
          model.url = () => url;
          model.saveModel({
            error: (model, response) => {
              const error = Messenger.getErrorFromResponse(response);
              model.set({
                isSafe: false,
                reason: error,
              });
              resolve(model);
            },
            success: (model) => resolve(model),
          });
        });
      },
      _allowSave: function () {
        // Empty response templates are added when the user views a particular language/touchpoint combination,
        // and they should not be saved because they only exist to drive the UI.
        if (
          this.isNew() &&
          this.isEmpty() &&
          (!this.commentThreadsCol || this.commentThreadsCol.length === 0) &&
          !this.requireSave
        ) {
          return false;
        }
        return TopicResponseModelResponseTemplateSuper._allowSave.apply(this, arguments);
      },
      _updateStatusValue: function () {
        const doUpdateStatusValue = (statusName) => {
          if (this.statusName != statusName) {
            //do not let the change dirty the topic, but do have it fire a change event
            const isDirty = this.dirty;
            this.statusValue = statusName;
            this.trigger("change");
            this.dirty = isDirty;
          }
        };

        if (this.isEmpty()) {
          doUpdateStatusValue($.t("topic.response.localeNotPublished"));
          return;
        }
        const allStatuses = Messenger.reqres.request("kb:statuses");
        allStatuses.onSynced(function () {
          const statusArray = allStatuses.get("allTopicStatuses");
          const statusId = this.get("statusId");
          for (let i = 0; i < statusArray.length; i++) {
            if (statusArray[i].statusId === statusId) {
              doUpdateStatusValue(statusArray[i].statusName);
              break;
            }
          }
        }, this);
      },
    },
    {
      EMPTY_RESPONSE_TEXT:
        "<responsetemplate><agent-utterance></agent-utterance></responsetemplate>",
      EMPTY_RESPONSE_TEXT_RE:
        /^(\s*(<responsetemplate><agent-utterance>)+\s*(<\/agent-utterance><\/responsetemplate>)+\s*|\s*)$/i,
      RESPONSE_PREFIX: RESPONSE_PREFIX,
      RESPONSE_SUFFIX: RESPONSE_SUFFIX,
      prepareResponseForEdit: function (response) {
        let firstIndex = 0;
        let lastIndex = response.length;
        if (response.startsWith(RESPONSE_PREFIX)) {
          firstIndex += RESPONSE_PREFIX.length;
        }
        if (response.endsWith(RESPONSE_SUFFIX)) {
          lastIndex -= RESPONSE_SUFFIX.length;
        }
        return response.substring(firstIndex, lastIndex);
      },
      prepareResponseForSave: function (response) {
        if (!response) {
          response = "";
        }
        if (response.startsWith(RESPONSE_PREFIX) && response.endsWith(RESPONSE_SUFFIX)) {
          return response;
        }
        return RESPONSE_PREFIX + response + RESPONSE_SUFFIX;
      },
    },
  );

  TopicResponseModel.responseTemplates = Backbone.Collection.extend({
    model: TopicResponseModel.responseTemplate,
    createResponseTemplate: function (attributes) {
      return new TopicResponseModel.responseTemplate(attributes, { parse: true });
    },
    addResponseTemplate: function (attributes) {
      const responseTemplate = this.createResponseTemplate(attributes);
      this.add(responseTemplate);
      return responseTemplate;
    },
  });

  return TopicResponseModel;
});
